import { Controller, useFormContext } from 'react-hook-form';
import { Info, X, ChevronDown, Check } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const SelectFormField = ({
  name,
  label,
  placeholder,
  required = false,
  options = [],
  rules = {},
  className = '',
  cols = 1,
  tooltip = '',
  onValueChange,
  defaultValue,
  isMulti = false,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const { t } = useTranslation(['common']);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const requiredRule = required
    ? { required: t('{{field}} est requis', { field: label }) }
    : {};

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMultiSelect = (selectedValue, currentValues, field) => {
    let newValues = Array.isArray(currentValues) ? [...currentValues] : [];

    const existingIndex = newValues.indexOf(selectedValue);

    if (existingIndex >= 0) {
      // Remove if already selected
      newValues.splice(existingIndex, 1);
    } else {
      // Add if not selected
      newValues.push(selectedValue);
    }

    field.onChange(newValues);
    setValue(name, newValues);
    onValueChange?.(newValues);
  };

  const handleSingleSelect = (selectedValue, field) => {
    field.onChange(selectedValue);
    setValue(name, selectedValue);
    onValueChange?.(selectedValue);
    setIsOpen(false);
  };

  const removeMultiItem = (valueToRemove, currentValues, field) => {
    const newValues = Array.isArray(currentValues)
      ? currentValues.filter((value) => value !== valueToRemove)
      : [];

    field.onChange(newValues);
    setValue(name, newValues);
    onValueChange?.(newValues);
  };

  const isSelected = (optionValue, fieldValue) => {
    if (isMulti) {
      return Array.isArray(fieldValue) && fieldValue.includes(optionValue);
    }
    return fieldValue === optionValue;
  };

  const getDisplayValue = (fieldValue) => {
    if (isMulti) {
      const values = Array.isArray(fieldValue) ? fieldValue : [];
      if (values.length === 0) return placeholder || `Sélectionner ${label}`;
      if (values.length === 1) {
        const option = options.find((opt) => opt.value === values[0]);
        return option ? option.label : values[0];
      }
      return `${values.length} éléments sélectionnés`;
    }

    const option = options.find((opt) => opt.value === fieldValue);
    return option ? option.label : placeholder || `Sélectionner ${label}`;
  };

  const getSelectedLabels = (fieldValue) => {
    if (!Array.isArray(fieldValue)) return [];
    return fieldValue.map((value) => {
      const option = options.find((opt) => opt.value === value);
      return option ? { value, label: option.label } : { value, label: value };
    });
  };

  return (
    <div className={`${cols > 1 ? `md:col-span-${cols}` : ''} ${className}`}>
      <Controller
        name={name}
        control={control}
        rules={{ ...requiredRule, ...rules }}
        render={({ field }) => (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-medium">
                {label} {required && <span className="text-red-500">*</span>}
              </label>
              {tooltip && (
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px]">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Multi-selection badges */}
            {isMulti &&
              Array.isArray(field.value) &&
              field.value.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {getSelectedLabels(field.value).map((item) => (
                    <Badge
                      key={item.value}
                      variant="secondary"
                      className="flex items-center gap-1 text-xs"
                    >
                      {item.label}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMultiItem(item.value, field.value, field);
                        }}
                      />
                    </Badge>
                  ))}
                </div>
              )}

            <div className="relative" ref={dropdownRef}>
              {/* Custom Select Trigger */}
              <Button
                type="button"
                variant="outline"
                className={`w-full justify-between h-10 px-3 py-2 text-sm font-normal border-2 ${
                  errors[name] ? 'border-red-500' : 'border-gray-400 dark:border-gray-600'
                } ${
                  !field.value ||
                  (isMulti &&
                    (!Array.isArray(field.value) || field.value.length === 0))
                    ? 'text-muted-foreground'
                    : ''
                }`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label={label}
                data-field-label={label}
                data-field-name={field.name}
              >
                <span className="truncate">{getDisplayValue(field.value)}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </Button>

              {/* Dropdown Menu */}
              {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-background border-2 border-gray-400 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {options.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Aucune option disponible
                    </div>
                  ) : (
                    options.map((option) => (
                      <div
                        key={option.value}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground flex items-center justify-between ${
                          isSelected(option.value, field.value)
                            ? 'bg-accent text-accent-foreground'
                            : 'text-foreground'
                        }`}
                        onClick={() => {
                          if (isMulti) {
                            handleMultiSelect(option.value, field.value, field);
                          } else {
                            handleSingleSelect(option.value, field);
                          }
                        }}
                      >
                        <span>{option.label}</span>
                        {isSelected(option.value, field.value) && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {errors[name] && (
              <p className="text-sm text-red-500 mt-1">
                {errors[name].message}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default React.memo(SelectFormField);
